import { getNearestLocations } from '../helpers/distance';
import { geocodeLocation } from '../helpers/geocode';
import { geocodingResultTemplate } from '../helpers/geocoding-result-template';
import Mapbox from '../helpers/mapbox';
import { bugsnagClient } from '@mrhenry/wp--bugsnag-config';

class MrLocationsMap extends HTMLElement {
	#lastQuery = '';

	#didInitMap = false;

	#observer = new IntersectionObserver( ( entries ) => {
		for ( let i = 0; i < entries.length; i += 1 ) {
			const entry = entries[i];

			if ( !entry || !entry.target ) {
				continue;
			}

			if ( entry.target === this && entry.isIntersecting ) {
				if ( this.#didInitMap ) {
					return;
				}

				// Any errors that happen below are fatal and cannot be retried.
				// No point to set this to true after actually creating a map.
				this.#didInitMap = true;

				if ( this.#observer ) {
					this.#observer.disconnect();
					this.#observer = null;
				}

				this.initMap();
				break;
			}
		}
	} );

	// Life cycle
	connectedCallback() {
		if ( !( 'IntersectionObserver' in window ) ) {
			return;
		}

		this.addEventListener( 'submit', this.#handleSearchSubmit );

		if ( this.#observer ) {
			this.#observer.observe( this );
		}
	}

	disconnectedCallback() {
		if ( this.#observer ) {
			this.#observer.disconnect();
		}

		this.removeEventListener( 'submit', this.#handleSearchSubmit );
	}

	async initMap() {
		try {
			await Promise.all( [
				Mapbox.loadDependencies(),
				this.trySetLocations(),
				new Promise( ( resolve, reject ) => {
					requestAnimationFrame( () => {
						if ( this.querySelector( '[data-store-locator-map]' ) ) {
							resolve();

							return;
						}

						reject( new Error( 'Map element not found' ) );
					} );
				} ),
			] );
		} catch ( err ) {
			console.warn( err );
			this.innerHTML = '<span style="user-select:none;"><br>&nbsp;&nbsp;❗️</span>';
		}

		// XXX_SECRET
		window.mapboxgl.accessToken = 'pk.eyJ1IjoibXJoZW5yeSIsImEiOiJjbXNvaTUzb3QwMHJpMndzZzhiaHNxOWMyIn0._AIDXMaYvpHZrIFs464lXw';

		this.renderMap();
	}

	async trySetLocations() {
		try {
			const url = new URL( '/wp-json/locations/all.json', window.location.href );
			const search = new URLSearchParams();
			search.set( 'lang', document.documentElement.lang.split( '-' )[0] );
			url.search = search.toString();

			const resp = await fetch( url );
			const data = await resp.json();
			if ( !data || !data.length ) {
				return;
			}

			this.locations = data;
		} catch ( err ) {
			console.dir( err );
			bugsnagClient.notify( err );
		}
	}

	renderMap() {
		const mapElement = this.querySelector( '[data-store-locator-map]' );
		if ( !mapElement ) {
			return;
		}

		const southWest = [
			2.51357303225,
			49.5294835476,
		];

		const northEast = [
			6.15665815596,
			51.4750237087,
		];

		const bounds = [
			southWest,
			northEast,
		];

		Mapbox.initializeMap( mapElement, bounds ).then( ( map ) => {
			this.map = map;
		} ).then( () => {
			Mapbox.renderMarkers( {
				type: 'FeatureCollection',
				features: this.locations,
			}, this.map );
			this.map.fitBounds( [
				southWest,
				northEast,
			], {
				animate: false,
			} );
		} );
	}

	#handleSearchSubmit = async( event ) => {
		event.preventDefault();
		event.stopPropagation();

		const searchQueryInputEl = this.querySelector( '[data-store-locator-search-input]' );
		if ( !searchQueryInputEl ) {
			this.clearResults();

			return;
		}

		const searchQuery = searchQueryInputEl.value?.trim();
		if ( !searchQuery ) {
			this.clearResults();

			return;
		}

		this.#lastQuery = searchQuery;

		try {
			const {
				coordinates, full_address,
			} = await geocodeLocation( searchQuery );

			// We allow users to quickly correct typos and re-submit.
			// Only render results of this response is still relevant for the last query.
			if ( this.#lastQuery !== searchQuery ) {
				return;
			}

			if ( !coordinates ) {
				this.showErrorMessage( 'not-found' );

				return;
			}

			this.focusOnLocation( coordinates );

			const results = getNearestLocations( coordinates, this.locations );
			if ( !results.length ) {
				this.showErrorMessage( 'no-results' );

				return;
			}

			this.renderResults( full_address, results );
		} catch ( err ) {
			console.warn( err );
			bugsnagClient.notify( err );

			if ( this.#lastQuery !== searchQuery ) {
				return;
			}

			this.showErrorMessage( 'something-went-wrong' );
		}
	};

	showErrorMessage( messageKind ) {
		this.clearResults();

		this.querySelector( '[data-store-locator-error-messages]' )?.removeAttribute( 'hidden' );

		Array.from( this.querySelectorAll( '[data-store-locator-error-message]' ) ).forEach( ( errorMessageEl ) => {
			if ( errorMessageEl.getAttribute( 'data-store-locator-error-message' ) === messageKind ) {
				errorMessageEl.removeAttribute( 'hidden' );

				return;
			}

			errorMessageEl.setAttribute( 'hidden', '' );
		} );
	}

	hideErrorMessage() {
		this.querySelector( '[data-store-locator-error-messages]' )?.setAttribute( 'hidden', '' );
	}

	showAddressMessage( address ) {
		const addressMessageEl = this.querySelector( '[data-store-locator-address-message]' );
		if ( !addressMessageEl ) {
			return;
		}

		const addressMessageContentEl = this.querySelector( '[data-store-locator-address-message-content]' );
		if ( !addressMessageContentEl ) {
			return;
		}

		addressMessageContentEl.textContent = address;

		addressMessageEl.removeAttribute( 'hidden' );
		addressMessageContentEl.removeAttribute( 'hidden' );
	}

	hideAddressMessage() {
		this.querySelector( '[data-store-locator-address-message]' )?.setAttribute( 'hidden', '' );
	}

	focusOnLocation( coordinates ) {
		if ( !this.map || !coordinates?.length ) {
			return;
		}

		this.map.flyTo( {
			center: coordinates,
			zoom: 10,
			speed: 1.2,
			curve: 1.42,
			essential: true,
		} );
	}

	renderResults( address, results ) {
		const resultsListEl = this.querySelector( '[data-store-locator-results-list]' );
		if ( !resultsListEl ) {
			return;
		}

		this.showAddressMessage( address );

		resultsListEl.innerHTML = '';

		const fragment = document.createDocumentFragment();

		results.forEach( ( result ) => {
			fragment.appendChild(
				geocodingResultTemplate(
					result.location,
					this.getAttribute( 'data-show-on-map-text' ) ?? 'Show on map',
					() => {
						this.selectRetailer( result.location );
					}
				)
			);
		} );

		resultsListEl.appendChild( fragment );

		resultsListEl.removeAttribute( 'hidden' );
		this.hideErrorMessage();
	}

	clearResults() {
		const resultsListEl = this.querySelector( '[data-store-locator-results-list]' );
		if ( resultsListEl ) {
			resultsListEl.innerHTML = '';
			resultsListEl.setAttribute( 'hidden', '' );
		}

		this.hideAddressMessage();
		this.hideErrorMessage();
	}

	selectRetailer( location ) {
		if (
			!this.map ||
			!location?.geometry?.coordinates?.length
		) {
			return;
		}

		this.scrollIntoView( {
			behavior: 'smooth',
			block: 'start',
		} );

		const coordinates = location.geometry.coordinates;
		const properties = location.properties ?? {};

		Array.from( this.querySelectorAll( '.mapboxgl-popup' ) ).forEach( ( popup ) => {
			popup.remove();
		} );

		this.map.flyTo( {
			center: coordinates,
			zoom: 16,
			speed: 1.2,
			curve: 1.42,
			essential: true,
		} );

		this.map.once( 'idle', () => {
			// eslint-disable-next-line no-undef
			new mapboxgl.Popup( {
				offset: 20,
			} )
				.setLngLat( coordinates )
				.setHTML( properties.popup_html )
				.addTo( this.map );
		} );
	}
}

customElements.define( 'mr-locations-map', MrLocationsMap );
